import { PaymentMethod, PaymentType } from '../../types/types';
import type { SummaryPaymentDetails } from '../../types/types';
import type { FormValues } from '@wix/form-viewer';
import { getBookerFormOverrides } from '../bookerDetails/bookerDetails';
import { getParticipantPayment } from '../multiParticipantFlow/participantPayment';
import { canCheckoutDirectly } from '../checkoutNavigation/canCheckoutDirectly';
import { slotServicesRequireStoredPaymentMethod } from '../payment';
import type {
  ParticipantPayment,
  SelectedProduct,
  SlotServices,
  WixForms,
  WixFormsFieldProperties,
} from '../state/types';

const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.values(value as object).every(isEmptyValue);
  }
  return false;
};

export const areRequiredFieldsFilled = ({
  fieldProperties = {},
  formValues = {},
  overrides = {},
}: {
  fieldProperties?: WixFormsFieldProperties;
  formValues?: FormValues;
  overrides?: WixForms['overrides'];
}): boolean =>
  [
    ...new Set([...Object.keys(fieldProperties), ...Object.keys(overrides)]),
  ].every((target) => {
    const field = { ...fieldProperties[target], ...overrides[target] };
    return !field.required || field.hidden || !isEmptyValue(formValues[target]);
  });

export const areAllFormsFilled = ({
  fieldProperties,
  wixFormValues,
  participantFormValues,
  bookerFormValues,
}: {
  fieldProperties?: WixFormsFieldProperties;
  wixFormValues: FormValues;
  participantFormValues?: FormValues[];
  bookerFormValues?: FormValues;
}): boolean => {
  const forms = participantFormValues?.length
    ? participantFormValues
    : [wixFormValues];
  if (
    !forms.every((formValues) =>
      areRequiredFieldsFilled({ fieldProperties, formValues }),
    )
  ) {
    return false;
  }
  if (!bookerFormValues) {
    return true;
  }
  return areRequiredFieldsFilled({
    fieldProperties,
    formValues: bookerFormValues,
    overrides: getBookerFormOverrides({
      fieldProperties: fieldProperties ?? {},
      overrides: {},
    }),
  });
};

export const hasPayLater = ({ payLater }: SummaryPaymentDetails): boolean =>
  (payLater ?? 0) > 0;

export enum ExpressCheckoutMode {
  Live = 'live',
  Control = 'control',
  Demo = 'demo',
  Off = 'off',
}

export const getExpressCheckoutMode = ({
  isEnabled,
  isSSR,
  isEditor,
  isPreview,
  isTemplate,
  isOwner,
}: {
  isEnabled: boolean;
  isSSR: boolean;
  isEditor: boolean;
  isPreview: boolean;
  isTemplate: boolean;
  isOwner: boolean;
}): ExpressCheckoutMode => {
  if (isSSR) {
    return ExpressCheckoutMode.Off;
  }
  if (isEditor || isPreview) {
    return isEnabled ? ExpressCheckoutMode.Demo : ExpressCheckoutMode.Off;
  }
  if (isTemplate || isOwner) {
    return ExpressCheckoutMode.Off;
  }
  return isEnabled ? ExpressCheckoutMode.Live : ExpressCheckoutMode.Control;
};

const isSupportedPayment = ({
  selectedPaymentOption,
  selectedPaymentType,
}: ParticipantPayment): boolean =>
  selectedPaymentOption?.type !== PaymentType.BUY_PLAN &&
  selectedPaymentOption?.type !== PaymentType.BUY_PLAN_AND_BOOK &&
  selectedPaymentOption?.type !== PaymentType.SUBSCRIPTION &&
  selectedPaymentType !== PaymentMethod.OFFLINE;

export const shouldShowExpressCheckout = ({
  mode,
  slotServices,
  selectedProductAddons,
  isSlotUnavailable,
  isCart,
  isServiceInCart,
  isUpsellPluginInstalled,
  summaryPaymentDetails,
}: {
  mode?: ExpressCheckoutMode;
  slotServices: SlotServices;
  selectedProductAddons?: SelectedProduct[];
  isSlotUnavailable?: boolean;
  isCart?: boolean;
  isServiceInCart?: boolean;
  isUpsellPluginInstalled?: boolean;
  summaryPaymentDetails: SummaryPaymentDetails;
}): boolean => {
  if (mode === ExpressCheckoutMode.Demo) {
    return true;
  }
  if (
    mode === ExpressCheckoutMode.Live ||
    mode === ExpressCheckoutMode.Control
  ) {
    return (
      !slotServicesRequireStoredPaymentMethod(slotServices) &&
      !selectedProductAddons?.length &&
      !isSlotUnavailable &&
      canCheckoutDirectly({
        isCart,
        isServiceInCart,
        isUpsellPluginInstalled,
      }) &&
      !hasPayLater(summaryPaymentDetails) &&
      Object.values(slotServices).every(
        (slotService) =>
          isSupportedPayment(slotService) &&
          slotService.participants?.every(
            (_, index) =>
              index === 0 ||
              isSupportedPayment(getParticipantPayment(slotService, index)),
          ) !== false,
      )
    );
  }
  return false;
};
